import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField, LoadingContent } from 'components'
import { VehicleConformityDetailsDto } from 'dtos/vehicles/conformity/index'
import { VehicleConformityDto } from 'dtos/vehicles/conformity/index'
import ConformityZipDownloadLink from 'pages/conformity/ConformityZipDownloadLink'
import ConformityZipPreviewLink from 'pages/conformity/ConformityZipPreviewLink'
import { useState } from 'react'
import { formatDateMoment } from 'utils'
import EditConformityDetailsDialog from './EditConformityDetailsDialog'
import { updateConformityDetails } from 'controllers/vehicles/conformity/index'
import { enqueueSnackbar } from 'notistack'

interface ConformityDetailsCardProps {
  conformityDetails?: VehicleConformityDetailsDto
  isLoading: boolean
  setState: (conformity: VehicleConformityDto) => void
  vehicleGuid?: string
}

export default function ConformityDetailsCard({ conformityDetails, isLoading, setState, vehicleGuid }: ConformityDetailsCardProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  return (
    <>
      <EditConformityDetailsDialog
        initialValues={conformityDetails}
        isLoading={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          updateConformityDetails(vehicleGuid!, values)
            .then(res => {
              enqueueSnackbar('Conformity Details Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              setState(res.value)
            })
            .finally(() => setIsSaving(false))
        }}
        open={dialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h6'>
                  Conformity Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button disabled={isLoading || !conformityDetails?.sentToDOTDateTime} endIcon={<Edit />} onClick={() => setDialogOpen(true)} variant='text'>
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <LoadingContent isLoading={isLoading}>
              <Grid item xs={12}>
                <LabelField
                  label='Sent To DOT'
                  value={conformityDetails?.sentToDOTDateTime ? formatDateMoment(conformityDetails.sentToDOTDateTime) : undefined}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Conformity Package Tracking #' value={conformityDetails?.conformityPackageTrackingNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Conformity ZIP Preview' value={<ConformityZipPreviewLink vehicleGuid={vehicleGuid} />} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Conformity ZIP Download' value={<ConformityZipDownloadLink vehicleGuid={vehicleGuid} vin={conformityDetails?.vin} />} />
              </Grid>
            </LoadingContent>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Conformity Details Card:**

The Conformity Details fields are populated after a user clicks the “Save and Mark as Sent to DOT” button in the Conformity feature.

Only the “Conformity Package Track #” may be edited after a vehicle’s conformity zip has been uploaded to the DOT portal.

**Edit Button:**

Clicking the Edit Button opens the Edit Conformity Details dialog.

This button is only enabled when "Sent to DOT" contains a value.

**Sent To DOT Label:**

Displays the date when the vehicle was marked as “Sent to DOT” via the Conformity screen.

**Conformity Package Tracking # Label:**

Displays the Conformity Package Tracking # entered for the vehicle via the Conformity screen.

**Conformity Zip Preview Link:**

Clicking “Preview” opens a browser tab or pop-up displaying each document and photo included in the vehicle’s Conformity Zip.

**Conformity Zip Download Link:**

Clicking “Download” downloads that vehicle’s Conformity Zip to the user’s machine.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
